export interface Maia2Prediction {
  move: string;
  probability: number;
  candidates: Array<{ move: string; probability: number }>;
  model: "maia2-rapid-fp16-v1";
}

export type Maia2Status = "loading_model" | "weighing_reply";

export interface Maia2PredictionOptions {
  signal?: AbortSignal;
  onStatus?: (status: Maia2Status) => void;
}

interface WorkerResponse extends Partial<Maia2Prediction> {
  id: number;
  move?: string;
  error?: string;
  status?: Maia2Status;
}

let worker: Worker | undefined;
let nextId = 1;
const pending = new Map<
  number,
  {
    resolve: (prediction: Maia2Prediction | null) => void;
    timeout: ReturnType<typeof setTimeout>;
    onStatus?: (status: Maia2Status) => void;
    cleanup: () => void;
  }
>();

const settle = (id: number, value: Maia2Prediction | null): void => {
  const request = pending.get(id);
  if (!request) return;
  clearTimeout(request.timeout);
  request.cleanup();
  pending.delete(id);
  request.resolve(value);
};

const getWorker = (): Worker => {
  if (worker) return worker;
  worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
    const request = pending.get(data.id);
    if (!request) return;
    if (data.status) {
      request.onStatus?.(data.status);
      return;
    }
    settle(
      data.id,
      data.error || !data.move || data.probability == null || !data.candidates || !data.model
        ? null
        : (data as Maia2Prediction),
    );
  };
  worker.onerror = () => {
    for (const request of pending.values()) {
      clearTimeout(request.timeout);
      request.cleanup();
      request.resolve(null);
    }
    pending.clear();
    worker?.terminate();
    worker = undefined;
  };
  return worker;
};

export const predictMaia2Position = (
  fen: string,
  activeElo: number,
  opponentElo: number,
  options: Maia2PredictionOptions = {},
): Promise<Maia2Prediction | null> =>
  new Promise((resolve) => {
    if (options.signal?.aborted) {
      resolve(null);
      return;
    }
    const id = nextId++;
    const timeout = setTimeout(() => {
      settle(id, null);
    }, 60_000);
    const abort = () => {
      getWorker().postMessage({ type: "cancel", id });
      settle(id, null);
    };
    options.signal?.addEventListener("abort", abort, { once: true });
    pending.set(id, {
      resolve,
      timeout,
      onStatus: options.onStatus,
      cleanup: () => options.signal?.removeEventListener("abort", abort),
    });
    getWorker().postMessage({ type: "predict", id, fen, activeElo, opponentElo });
  });
